import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Edit, ToggleOn, ToggleOff } from '@mui/icons-material';
import { usePeriodos, useTogglePeriodo } from '../../hooks/usePeriodos';
import PeriodoDialog from './PeriodoDialog';
import { getPeriodoLabel, type Periodo } from '../../types/periodo';
import { formatDate } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';

export default function PeriodosList() {
  const { effectiveRole } = useAuth();
  const { data: periodos = [], isLoading, isError, error } = usePeriodos(true);
  const toggle = useTogglePeriodo();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Periodo | null>(null);

  const canEdit = effectiveRole === 'admin_general';

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (periodo: Periodo) => {
    setEditing(periodo);
    setDialogOpen(true);
  };

  const handleToggle = async (p: Periodo) => {
    const accion = p.estado === 'activo' ? 'desactivar' : 'activar';
    if (!confirm(`Confirmas ${accion} el periodo "${getPeriodoLabel(p)}"?`)) return;
    await toggle.mutateAsync({ id: p.id, activo: p.estado !== 'activo' });
  };

  const columns = useMemo<GridColDef<Periodo>[]>(
    () => [
      {
        field: 'periodo',
        headerName: 'Periodo',
        width: 110,
        renderCell: (p) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
            {p.value}
          </Typography>
        ),
      },
      {
        field: 'label',
        headerName: 'Descripcion',
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: (p: GridRenderCellParams<Periodo>) => (
          <Typography variant="body2" fontWeight={500}>
            {getPeriodoLabel(p.row)}
          </Typography>
        ),
      },
      {
        field: 'fecha_vencimiento',
        headerName: 'Vencimiento',
        width: 130,
        renderCell: (p) => formatDate(p.value as string),
      },
      {
        field: 'version',
        headerName: 'Ver.',
        width: 70,
        renderCell: (p) => `v${p.value}`,
      },
      {
        field: 'excepcional',
        headerName: 'Tipo',
        width: 130,
        renderCell: (p: GridRenderCellParams<Periodo>) =>
          p.value ? (
            <Chip label="Excepcional" size="small" color="warning" />
          ) : (
            <Chip label="Mensual" size="small" variant="outlined" />
          ),
      },
      {
        field: 'estado',
        headerName: 'Estado',
        width: 110,
        renderCell: (p: GridRenderCellParams<Periodo>) => (
          <Chip
            label={p.value === 'activo' ? 'Activo' : 'Inactivo'}
            size="small"
            color={p.value === 'activo' ? 'success' : 'default'}
          />
        ),
      },
      ...(canEdit
        ? [
            {
              field: 'actions',
              headerName: 'Acciones',
              width: 130,
              sortable: false,
              filterable: false,
              renderCell: (p: GridRenderCellParams<Periodo>) => (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => openEdit(p.row)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={p.row.estado === 'activo' ? 'Desactivar' : 'Activar'}
                  >
                    <IconButton
                      size="small"
                      color={p.row.estado === 'activo' ? 'warning' : 'success'}
                      onClick={() => handleToggle(p.row)}
                    >
                      {p.row.estado === 'activo' ? (
                        <ToggleOn fontSize="small" />
                      ) : (
                        <ToggleOff fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            } as GridColDef<Periodo>,
          ]
        : []),
    ],
    [canEdit]
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Periodos de liquidacion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catalogo de periodos mensuales y excepcionales (aguinaldos, bonos)
          </Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Nuevo periodo
          </Button>
        )}
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Los periodos <strong>mensuales</strong> agrupan la liquidacion regular
        de aportes. Los periodos <strong>excepcionales</strong> permiten
        registrar aguinaldos, bonos fuera de fecha o reliquidaciones.
      </Alert>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar periodos: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={periodos}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default' },
          }}
          localeText={{
            noRowsLabel: 'No hay periodos cargados',
            footerTotalRows: 'Total:',
            MuiTablePagination: {
              labelRowsPerPage: 'Por pagina:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
            },
          }}
        />
      </Card>

      <PeriodoDialog
        open={dialogOpen}
        periodo={editing}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
